import { useEffect, useRef, useState } from "react";
import {
  Correction,
} from "../lib/corrections";
import { logUserEvent } from "../lib/events";
import { supabase } from "../lib/supabase";

type Stage = "input" | "saving" | "saved" | "error";

interface Props {
  meetingId: string;
  workspaceId: string;
  // The verbatim text of the span being corrected (joined token text).
  // Pre-fills the textarea so the user starts editing rather than retyping.
  originalText: string;
  // The span boundary the correction will apply to. Token-atomic --
  // selection that partially overlaps tokens snaps to the full overlapping
  // range upstream of this component. Sub-token correction is rejected
  // per the verbatim invariant.
  startMs: number;
  endMs: number;
  // The flag id links the correction back to its originating sentinel flag
  // (corrections.flag_id FK; per DECISIONS.md "Correction UI" Q5).
  // Always non-null: the only caller is the FlagRow Correct button.
  flagId: string;
  // Optional callback fired after the correction row lands successfully.
  // Used by FlagRow to flip the flag to status='corrected' in the same
  // optimistic-update window.
  onSaved?: (correction: Correction) => void;
  onClose: () => void;
}

// Inline correction popover: text input pre-filled with original_text,
// Save / Cancel, optional "promote to glossary" checkbox.
//
// Invoked from the FlagRow Correct button. The correction links back via
// flag_id; after save, the flag flips to status='corrected'.
//
// Click outside / Esc = cancel. Enter (no shift) = save; shift+Enter inserts
// a newline (corrected_text can be multiline).
export function CorrectionPopover({
  meetingId,
  workspaceId,
  originalText,
  startMs,
  endMs,
  flagId,
  onSaved,
  onClose,
}: Props) {
  const [text, setText] = useState(originalText);
  const [promote, setPromote] = useState(false);
  const [stage, setStage] = useState<Stage>("input");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Click outside the popover = cancel. Doesn't fire while saving.
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (stage === "saving") return;
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(e.target as Node)) return;
      onClose();
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [stage, onClose]);

  // After a successful save, hold "Saved" for ~1.5s then close.
  useEffect(() => {
    if (stage !== "saved") return;
    const t = window.setTimeout(onClose, 1500);
    return () => window.clearTimeout(t);
  }, [stage, onClose]);

  async function save() {
    const trimmed = text.trim();
    if (!trimmed) {
      setErrorMsg("Corrected text can't be empty.");
      setStage("error");
      return;
    }
    if (trimmed === originalText) {
      setErrorMsg("Corrected text is identical to the original.");
      setStage("error");
      return;
    }
    setStage("saving");
    setErrorMsg(null);
    try {
      // Read the flag's current status BEFORE any writes, so we can capture
      // it into pre_correction_status. This lets the WYSIWYG re-edit flow
      // revert the flag to its pre-correction state when dismissing this
      // correction. See docs/superpowers/specs/2026-05-29-wysiwyg-paragraph-editing-design.md
      // Phase 0 section for rationale.
      const { data: flagRow, error: flagReadError } = await supabase
        .from("flags")
        .select("status")
        .eq("id", flagId)
        .single();
      if (flagReadError) throw flagReadError;
      const preCorrectionStatus: string = flagRow.status;

      const insertPayload: Record<string, unknown> = {
        meeting_id: meetingId,
        workspace_id: workspaceId,
        flag_id: flagId,
        start_ms: startMs,
        end_ms: endMs,
        original_text: originalText,
        corrected_text: trimmed,
        status: "accepted",
        promote_to_glossary: promote,
        pre_correction_status: preCorrectionStatus,
      };

      const { data: newCorrection, error: insertError } = await supabase
        .from("corrections")
        .insert(insertPayload)
        .select()
        .single();
      if (insertError) throw insertError;

      const correction = newCorrection as Correction;

      // Flag flip happens AFTER the insert. If this fails, the correction
      // row exists but the flag's status is unchanged; this is recoverable
      // via the existing reverse-correction flow. Pre-WYSIWYG behavior preserved.
      await supabase
        .from("flags")
        .update({ status: "corrected" })
        .eq("id", flagId);
      try {
        await logUserEvent({
          meetingId,
          workspaceId,
          eventType: "text_correction_accept",
          payload: { correction_id: correction.id, original_text: originalText, corrected_text: trimmed },
        });
      } catch { /* best-effort */ }
      onSaved?.(correction);
      setStage("saved");
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
      setStage("error");
    }
  }

  if (stage === "saved") {
    return (
      <div ref={wrapperRef} className="correction-popover">
        <span className="ok" style={{ fontSize: 12 }}>
          ✓ Correction saved
        </span>
      </div>
    );
  }

  const saving = stage === "saving";
  return (
    <div
      ref={wrapperRef}
      className="correction-popover"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          fontSize: 11,
          color: "var(--text-muted)",
          marginBottom: 4,
        }}
      >
        Original:{" "}
        <span dir="auto" style={{ fontFamily: "monospace" }}>
          {originalText}
        </span>
      </div>
      <textarea
        autoFocus
        dir="auto"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            save();
          } else if (e.key === "Escape") {
            e.preventDefault();
            if (!saving) onClose();
          }
        }}
        disabled={saving}
        rows={2}
        placeholder="Corrected text"
        style={{
          width: 280,
          padding: "6px 8px",
          background: "var(--surface)",
          color: "var(--text)",
          border: "1px solid var(--border)",
          borderRadius: 4,
          fontSize: 13,
          fontFamily: "inherit",
          resize: "vertical",
          minHeight: 32,
        }}
      />
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          color: "var(--text-muted)",
          margin: 0,
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={promote}
          onChange={(e) => setPromote(e.target.checked)}
          disabled={saving}
          style={{ margin: 0 }}
        />
        Promote to glossary
      </label>
      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          style={{ padding: "4px 10px", fontSize: 12 }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          className="secondary"
          onClick={() => !saving && onClose()}
          disabled={saving}
          style={{ padding: "4px 10px", fontSize: 12 }}
        >
          Cancel
        </button>
      </div>
      {stage === "error" && errorMsg && (
        <span className="error" style={{ fontSize: 12 }}>{errorMsg}</span>
      )}
    </div>
  );
}
